import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { useSocialSchedulerApi } from '../../../../common/hooks/use-social-scheduler-api.hook';
import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import { SocialAccount } from '../../../../common/models/social-scheduler.model';
import { CsAsset, CsProject } from '../../../../common/models/content-studio.model';
import { PostForm, PostFormValues } from '../../../../modules/vendor/social-scheduler/components/PostForm';
import { PageContainer } from '../../../../common/components/PageContainer';
import { ReactComponent as CalendarIcon } from '../../../../assets/icons/calendar-icon.svg';

export const SocialCreatePostPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const api = useSocialSchedulerApi();
    const csApi = useContentStudioApi();

    const [accounts, setAccounts] = useState<SocialAccount[]>([]);
    const [assets, setAssets] = useState<CsAsset[]>([]);
    const [projects, setProjects] = useState<CsProject[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loadData = useCallback(async () => {
        try {
            const [accRes, assetRes, projectRes] = await Promise.all([
                api.listAccounts({ status: 'active' }),
                csApi.listAssets({ status: 'ready', limit: 100 } as any),
                csApi.listProjects({ limit: 100 }),
            ]);
            setAccounts(accRes.items || []);
            setAssets((assetRes as any).items || []);
            setProjects(projectRes.items || []);
        } catch (err) {
            console.error('Failed to load data:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const handleSubmit = async (values: PostFormValues, asDraft: boolean) => {
        setSubmitting(true);
        try {
            await api.createPost({
                assetUuid: values.selectedAssetUuids[0],
                assetUuids: values.selectedAssetUuids.length > 1 ? values.selectedAssetUuids : undefined,
                platform: values.platform,
                accountUuid: values.accountUuid,
                caption: values.caption,
                hashtags: values.hashtags,
                scheduledAt: new Date(values.scheduledAt).toISOString(),
                status: asDraft ? 'draft' : 'scheduled',
            });
            navigate('/marketing/social-scheduler');
        } catch (err) {
            console.error('Failed to create post:', err);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PageContainer
                breadcrumbs={[
                    { label: t('SIDEBAR.MARKETING.TITLE', 'Marketing') },
                    {
                        label: t('SIDEBAR.MARKETING.PLANNER', 'Planner'),
                        icon: <CalendarIcon style={{ width: 20, height: 20 }} />,
                    },
                    { label: t('SOCIAL_SCHEDULER.NEW_POST') },
                ]}
            >
                <div className="py-16 text-center text-sm text-muted-foreground">
                    {t('SOCIAL_SCHEDULER.LOADING', 'Loading...')}
                </div>
            </PageContainer>
        );
    }

    const dateParam = searchParams.get('date');
    // Arriving from a canvas picture: it is already chosen.
    const assetParam = searchParams.get('asset');
    const initial: Partial<PostFormValues> | undefined = dateParam || assetParam
        ? {
            ...(dateParam ? { scheduledAt: `${dateParam}T12:00` } : {}),
            ...(assetParam ? { selectedAssetUuids: [assetParam] } : {}),
        }
        : undefined;

    return (
        <PageContainer breadcrumbs={[{ label: t('SIDEBAR.MARKETING.TITLE') }, { label: t('SIDEBAR.MARKETING.PLANNER'), icon: <CalendarIcon style={{ width: 20, height: 20 }} /> }, { label: t('SOCIAL_SCHEDULER.NEW_POST') }]}>
        <div className="mx-auto max-w-[1200px]">
            {/* Header */}
            <div className="mb-6 flex items-center gap-3">
                <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={() => navigate('/marketing/social-scheduler')}
                >
                    <ArrowLeft className="rtl:rotate-180" />
                    {t('SOCIAL_SCHEDULER.BACK', 'Back')}
                </Button>
            </div>

            <PostForm
                accounts={accounts}
                assets={assets}
                projects={projects}
                initial={initial}
                onSubmit={handleSubmit}
                submitting={submitting}
            />
        </div>
        </PageContainer>
    );
};
